import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { CrudItemBase } from '@/components/admin/types/crud';
import { toast } from "@/components/ui/sonner";
import { supabase } from '@/integrations/supabase/client';

interface CrudOptions {
  tableName: string;
  queryKey: QueryKey;
  selectQuery?: string;
  order?: { column: string; options?: { ascending: boolean } };
}

export function useCrud<T extends CrudItemBase>({
  tableName,
  queryKey,
  selectQuery = '*',
  order,
}: CrudOptions) {
  const queryClient = useQueryClient();

  const {
    data: items,
    isLoading,
    error,
  } = useQuery<T[], Error>({
    queryKey,
    queryFn: async () => {
      let query = supabase.from(tableName as any).select(selectQuery) as any;
      if (order) {
        query = query.order(order.column, order.options);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data as T[]) || [];
    },
  });

  const createItem = useMutation({
    mutationFn: async (item: Omit<T, 'id' | 'created_at' | 'updated_at' | 'version' | 'is_latest_version'>) => {
      const { data, error } = await supabase.from(tableName as any).insert(item as any).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(`${tableName.slice(0, -1).replace(/_/g, ' ')} created successfully.`);
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`);
    },
  });

  const updateItem = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<T> }) => {
      const { data, error } = await supabase.from(tableName as any).update(updates as any).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(`${tableName.slice(0, -1).replace(/_/g, ' ')} updated successfully.`);
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(tableName as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(`${tableName.slice(0, -1).replace(/_/g, ' ')} deleted successfully.`);
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  return {
    items: items ?? [],
    isLoading,
    error,
    createItem: createItem.mutateAsync,
    updateItem: updateItem.mutateAsync,
    deleteItem: deleteItem.mutateAsync,
    isCreating: createItem.isPending,
    isUpdating: updateItem.isPending,
    isDeleting: deleteItem.isPending,
  };
}
